import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  useTheme,
  Skeleton,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import ModalMAIQ from 'app/components/ModalMAIQ';
import ConfirmationModal from 'app/components/ConfirmationModal';
import { ValueContent } from 'app/apiService/valueContent/valueContentApiService';
import ValueTypeEnums from './ValueTypeEnums';

interface DealerValueDetailsModalProps {
  open: boolean;
  onClose: () => void;
  data: ValueContent;
  onEdit: () => void;
  onDelete: () => void;
  onSave: (updated: ValueContent) => Promise<void>;
  isEditing: boolean;
  setIsEditing: (val: boolean) => void;
  setRefreshKey: React.Dispatch<React.SetStateAction<number>>;
}

const applicableForOptions = ['CPO', 'New', 'Used', 'All'];

const DealerValueDetailsModal: React.FC<DealerValueDetailsModalProps> = ({
  open,
  onClose,
  data,
  onEdit,
  onDelete,
  onSave,
  isEditing,
  setIsEditing,
  setRefreshKey,
}) => {
  const [formData, setFormData] = useState<ValueContent>({
    ...data,
    applicable_for: data.applicable_for || [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  React.useEffect(() => {
    setFormData({
      ...data,
      applicable_for: data.applicable_for || [],
    });
  }, [data]);

  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]:
        field === 'applicable_for'
          ? Array.isArray(value)
            ? value
            : [value]
          : value,
    }));
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      await onSave(formData);
      setRefreshKey(prev => prev + 1);
      onClose(); // Close modal after save
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData(data);
    setIsEditing(false);
  };

  return (
    <ModalMAIQ open={open} onClose={onClose} hideDefaultCloseButton={true}>
      <Paper
        elevation={0}
        sx={{
          width: '800px',
          maxWidth: '90vw',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            backgroundColor: '#0F2A52',
            color: 'white',
            zIndex: 10,
            flexShrink: 0,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Typography variant="h6" fontWeight="600">
              Dealer Value Details
            </Typography>
            <Box>
              {!isEditing && (
                <Tooltip title="Edit Dealer Value">
                  <IconButton
                    sx={{ color: 'white', p: 0.5 }}
                    size="small"
                    onClick={onEdit}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Delete Dealer Value">
                <IconButton
                  sx={{ color: 'white', p: 0.5, ml: 1 }}
                  size="small"
                  onClick={() => setDeleteModalOpen(true)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Close">
                <IconButton
                  aria-label="close"
                  onClick={onClose}
                  sx={{ color: 'white', p: 0.5, ml: 1 }}
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Headline"
              value={formData.headline}
              onChange={e => handleFormChange('headline', e.target.value)}
              InputProps={{ readOnly: !isEditing }}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Content"
              value={formData.content}
              onChange={e => handleFormChange('content', e.target.value)}
              multiline
              rows={4}
              InputProps={{ readOnly: !isEditing }}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Label"
              value={formData.label || ''}
              onChange={e => handleFormChange('label', e.target.value)}
              placeholder="Enter a short label (max 40 characters)"
              InputProps={{ 
                readOnly: !isEditing,
                inputProps: { maxLength: 40 }
              }}
              helperText={`${(formData.label || '').length}/40 characters`}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.value_type}
                onChange={e => handleFormChange('value_type', e.target.value)}
                label="Type"
                disabled={!isEditing}
              >
                {ValueTypeEnums.getEnumValues().map(valueType => (
                  <MenuItem key={valueType} value={valueType}>
                    {valueType.toUpperCase().replace('_', ' ')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Applicable For</InputLabel>
              <Select
                multiple
                value={formData.applicable_for || []}
                onChange={e =>
                  handleFormChange('applicable_for', e.target.value)
                }
                label="Applicable For"
                renderValue={selected => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected || []).map((value: string) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
                disabled={!isEditing}
              >
                {applicableForOptions.map(option => (
                  <MenuItem key={option} value={option}>
                    <Checkbox
                      checked={
                        (formData.applicable_for || []).indexOf(option) > -1
                      }
                    />
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
               <DatePicker
                 label="Expiry Date"
                 value={formData.end_date ? dayjs(formData.end_date) : null}
                 onChange={(newValue: Dayjs | null) =>
                   handleFormChange('end_date', newValue ? newValue.format('YYYY-MM-DD') : '')
                 }
                 disabled={!isEditing}
                 slotProps={{
                   textField: {
                     fullWidth: true,
                     size: 'small',
                     InputLabelProps: { shrink: true },
                    //  InputProps: { readOnly: !isEditing },
                     sx: { mb: 2 },
                   },
                 }}
               />
              </LocalizationProvider>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_active}
                  onChange={e =>
                    handleFormChange('is_active', e.target.checked)
                  }
                  disabled={!isEditing}
                />
              }
              label="Active"
            />
          </Box>
        </Box>

        {/* Footer */}
        <Divider />
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'flex-end',
            backgroundColor: 'white',
            borderTop: '1px solid rgba(0, 0, 0, 0.12)',
            zIndex: 9,
            flexShrink: 0,
            gap: 2,
          }}
        >
          {isEditing && (
            <>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={isSubmitting}
                sx={{
                  textTransform: 'none',
                  fontWeight: 'bold',
                  py: 0.75,
                  px: 3,
                }}
              >
                Cancel
              </Button>
              {isSubmitting ? (
                <Skeleton
                  variant="rectangular"
                  width={140}
                  height={36}
                  sx={{
                    borderRadius: 1,
                    bgcolor: 'rgba(15, 40, 76, 0.1)',
                  }}
                />
              ) : (
                <Tooltip title="Save your changes">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    startIcon={<SaveIcon />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 'bold',
                      bgcolor: '#0F2A52',
                      color: 'white',
                      py: 0.75,
                      px: 3,
                      '&:hover': {
                        bgcolor: '#143362',
                      },
                    }}
                  >
                    Save Changes
                  </Button>
                </Tooltip>
              )}
            </>
          )}
        </Box>

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          open={deleteModalOpen}
          title="Delete Dealer Value"
          message="Are you sure you want to delete this dealer value? This action cannot be undone."
          onConfirm={onDelete}
          onClose={() => setDeleteModalOpen(false)}
        />
      </Paper>
    </ModalMAIQ>
  );
};

export default DealerValueDetailsModal;
